<template>
  <section class="rounded-lg border bg-card p-6">
    <div class="flex items-center justify-between gap-3">
      <h3 class="text-base font-medium">
        Status Flow
      </h3>
      <TaskStatusField :value="task.status" />
    </div>

    <p class="mt-2 text-sm text-muted-foreground">
      {{ helperText }}
    </p>

    <div v-if="availableActions.length > 0" class="mt-4 flex flex-wrap gap-2">
      <Button
        v-for="action in availableActions"
        :key="`${normalizedStatus}-${action.to}`"
        type="button"
        size="sm"
        :variant="action.variant"
        :loading="isUpdating"
        :disabled="isUpdating"
        @click="transitionToStatus(action)"
      >
        {{ action.label }}
      </Button>
    </div>

    <p v-else class="mt-4 text-sm text-muted-foreground">
      {{ noActionsText }}
    </p>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { toast } from 'vue-sonner'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import TaskStatusField from './fields/TaskStatusField.vue'
import { useTasksStore } from '../../stores/tasksStore.js'

const STATUS_LABELS = {
  draft: 'Draft',
  open: 'To Do',
  assigned: 'Assigned',
  accepted: 'Accepted',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  pending_approval: 'Sent for Approval',
  review_rejected: 'Review Rejected',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

const STATUS_HELPERS = {
  draft: 'Get this task ready for execution.',
  open: 'Task is ready to be picked up.',
  assigned: 'Task is assigned and waiting for acceptance or start.',
  accepted: 'Task is acknowledged and ready to begin.',
  in_progress: 'Work is currently in progress.',
  blocked: 'Task is blocked and needs unblocking to continue.',
  pending_approval: 'Work is done and waiting for approval.',
  review_rejected: 'Review was rejected and requires rework.',
  completed: 'Task lifecycle is complete.',
  cancelled: 'Task was cancelled. Restart it to continue work.',
}

const STATUS_ACTION_UI = {
  draft: {
    in_progress: { label: 'Start Task', variant: 'default' },
    assigned: { label: 'Assign', variant: 'secondary' },
    cancelled: { label: 'Cancel Task', variant: 'destructive', confirmMessage: 'Cancel this task?' },
  },
  open: {
    in_progress: { label: 'Start Work', variant: 'default' },
    assigned: { label: 'Assign', variant: 'secondary' },
    cancelled: { label: 'Cancel Task', variant: 'destructive', confirmMessage: 'Cancel this task?' },
  },
  assigned: {
    accepted: { label: 'Accept', variant: 'default' },
    in_progress: { label: 'Start Work', variant: 'secondary' },
    cancelled: { label: 'Cancel Task', variant: 'destructive', confirmMessage: 'Cancel this task?' },
  },
  accepted: {
    in_progress: { label: 'Start Work', variant: 'default' },
    blocked: { label: 'Mark Blocked', variant: 'secondary' },
    cancelled: { label: 'Cancel Task', variant: 'destructive', confirmMessage: 'Cancel this task?' },
  },
  in_progress: {
    pending_approval: { label: 'Send for Approval', variant: 'default' },
    blocked: { label: 'Mark Blocked', variant: 'secondary' },
    cancelled: { label: 'Cancel Task', variant: 'destructive', confirmMessage: 'Cancel this task?' },
  },
  blocked: {
    in_progress: { label: 'Resume Work', variant: 'default' },
    cancelled: { label: 'Cancel Task', variant: 'destructive', confirmMessage: 'Cancel this task?' },
  },
  pending_approval: {
  },
  review_rejected: {
    in_progress: { label: 'Resume Work', variant: 'default' },
    cancelled: { label: 'Cancel Task', variant: 'destructive', confirmMessage: 'Cancel this task?' },
  },
  completed: {
    in_progress: { label: 'Send Back to In Progress', variant: 'secondary' },
  },
  cancelled: {
    in_progress: { label: 'Restart Task', variant: 'default' },
  },
}
const REVIEW_SENDABLE_STATUSES = ['not_submitted', 'rejected', 'withdrawn']

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  context: {
    type: String,
    default: 'task-show',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
})

const tasksStore = useTasksStore(props.context)
const taskId = computed(() => Number(props.task?.id))
const isUpdating = computed(() => tasksStore.isUpdatingStatus(taskId.value))
const reviewProcess = computed(() => props.task?.review_process ?? props.task?.reviewProcess ?? null)
const normalizedStatus = computed(() => {
  if (typeof props.task?.status === 'string') {
    return props.task.status
  }

  if (props.task?.status && typeof props.task.status === 'object' && 'value' in props.task.status) {
    return String(props.task.status.value)
  }

  return null
})
const reviewProcessStatus = computed(() => {
  if (typeof reviewProcess.value?.status === 'string') {
    return reviewProcess.value.status
  }

  if (reviewProcess.value?.status && typeof reviewProcess.value.status === 'object' && 'value' in reviewProcess.value.status) {
    return String(reviewProcess.value.status.value)
  }

  return null
})
const hasReviewProcess = computed(() => {
  const reviewProcessId = Number(reviewProcess.value?.id)

  return Number.isInteger(reviewProcessId) && reviewProcessId > 0
})
const canSendForApproval = computed(() => {
  if (!hasReviewProcess.value || !reviewProcessStatus.value) {
    return false
  }

  return REVIEW_SENDABLE_STATUSES.includes(reviewProcessStatus.value)
})

const statusTransitionOptions = computed(() => {
  const transitions = props.task?.allowed_status_transitions ?? props.task?.allowedStatusTransitions

  if (!Array.isArray(transitions)) {
    return []
  }

  return transitions
    .filter((transition) => transition && typeof transition === 'object' && typeof transition.value === 'string')
    .map((transition) => ({
      value: transition.value,
      label: typeof transition.label === 'string' && transition.label.trim() !== ''
        ? transition.label
        : (STATUS_LABELS[transition.value] ?? transition.value),
    }))
})

const availableActions = computed(() => {
  if (props.readonly) {
    return []
  }

  if (!normalizedStatus.value) {
    return []
  }

  if (normalizedStatus.value === 'pending_approval') {
    return []
  }

  const currentStatusActions = STATUS_ACTION_UI[normalizedStatus.value] ?? {}
  const mapTransitionToAction = (transition) => {
    const actionUi = currentStatusActions[transition.value] ?? {}
    let label = actionUi.label ?? transition.label ?? transition.value

    if (
      normalizedStatus.value === 'in_progress'
      && transition.value === 'pending_approval'
      && reviewProcessStatus.value !== null
      && ['rejected', 'withdrawn'].includes(reviewProcessStatus.value)
    ) {
      label = 'Resubmit for Approval'
    }

    return {
      to: transition.value,
      label,
      variant: actionUi.variant ?? 'secondary',
      confirmMessage: actionUi.confirmMessage,
    }
  }
  const shouldKeepAction = (action) => {
    if (action.to === 'completed' && hasReviewProcess.value) {
      return false
    }

    if (normalizedStatus.value !== 'in_progress' || action.to !== 'pending_approval') {
      return true
    }

    return canSendForApproval.value
  }

  if (statusTransitionOptions.value.length > 0) {
    return statusTransitionOptions.value
      .map(mapTransitionToAction)
      .filter(shouldKeepAction)
  }

  return Object.entries(currentStatusActions)
    .map(([to, actionUi]) => mapTransitionToAction({
      value: to,
      label: actionUi.label ?? STATUS_LABELS[to] ?? to,
    }))
    .filter(shouldKeepAction)
})

const noActionsText = computed(() => {
  if (!normalizedStatus.value) {
    return 'Status is currently unknown.'
  }

  if (props.readonly) {
    return 'No status actions are available right now.'
  }

  if (normalizedStatus.value === 'pending_approval') {
    return 'Review is in progress. Use the Review Process card to approve, reject, or withdraw.'
  }

  if (normalizedStatus.value === 'in_progress' && !hasReviewProcess.value) {
    return 'Set up a review process to send this task for approval.'
  }

  if (normalizedStatus.value === 'in_progress' && reviewProcessStatus.value === 'cancelled') {
    return 'The review process is cancelled. Re-enable it before sending this task for approval.'
  }

  if (normalizedStatus.value === 'completed') {
    return 'This task is in a terminal status.'
  }

  return 'No status actions are available right now.'
})

const helperText = computed(() => {
  if (!normalizedStatus.value) {
    return 'Status is currently unknown.'
  }

  if (normalizedStatus.value === 'pending_approval') {
    return 'Task is waiting for a final review decision.'
  }

  if (normalizedStatus.value === 'in_progress' && !hasReviewProcess.value) {
    return 'Work is currently in progress. Configure a review process before sending for approval.'
  }

  if (normalizedStatus.value === 'in_progress' && reviewProcessStatus.value === 'cancelled') {
    return 'Work is in progress, but the linked review process is currently cancelled.'
  }

  return STATUS_HELPERS[normalizedStatus.value] ?? 'Manage the next step in this task lifecycle.'
})

const transitionToStatus = async (action) => {
  if (props.readonly) {
    return
  }

  if (!Number.isInteger(taskId.value) || taskId.value <= 0) {
    return
  }

  if (action.confirmMessage && !window.confirm(action.confirmMessage)) {
    return
  }

  try {
    await tasksStore.updateStatus(taskId.value, action.to)

    try {
      await tasksStore.fetchItemById(taskId.value)
    } catch {
      // Keep the successful status update visible even if the refresh fails.
    }

    toast.success(`Status changed to ${STATUS_LABELS[action.to] ?? action.to}.`)
  } catch (error) {
    const errorMessage = error?.response?.data?.errors?.status?.[0]
      || error?.response?.data?.message
      || 'Unable to update task status.'
    toast.error(errorMessage)

    try {
      await tasksStore.fetchItemById(taskId.value)
    } catch {
      // Keep the original API error feedback if refetch fails.
    }
  }
}
</script>
